/**
 * OutboundInvestigations — „Zu prüfende Lieferaufträge" (#1650), Phase 1 = DRY-RUN.
 *
 * Reine Anzeige der vom Poller erkannten OUT-Pickings mit OMS-`to_investigate_note`.
 * Phase 1 erzeugt KEINEN Case + KEINE Notification — die Seite spiegelt nur, was
 * der Dry-Run getrackt hat (Status would_create/seeded/would_close/…).
 *
 * @page /operations/lieferauftraege-pruefung
 * @api  /api/outbound-investigations*
 * @i18n outboundInv.*
 * @since v2.797.0 / #1650
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { PackageSearch, Search, Loader2, X, PlusCircle, Archive, CheckCircle2, XCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(path, {
    ...opts,
    credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    let data;
    try { data = await r.json(); } catch { throw new Error(`HTTP ${r.status}`); }
    if (!r.ok) throw new Error(data.message || data.error || `HTTP ${r.status}`);
    return data;
  });
}

const STATUS_META = {
  seeded:       'bg-slate-50 text-slate-600 border-slate-200',
  would_create: 'bg-amber-50 text-amber-700 border-amber-200',
  skipped:      'bg-slate-100 text-slate-500 border-slate-300',
  created:      'bg-blue-50 text-blue-700 border-blue-200',
  would_close:  'bg-indigo-50 text-indigo-700 border-indigo-200',
  closed:       'bg-slate-50 text-slate-600 border-slate-200',
  resolved:     'bg-emerald-50 text-emerald-700 border-emerald-200',
};

function StatusBadge({ status, t }) {
  const cls = STATUS_META[status] || STATUS_META.seeded;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${cls}`}>
      {t(`outboundInv.status.${status}`) || status}
    </span>
  );
}

function fmtDate(s, t) {
  if (!s) return t ? t('outboundInv.never') : '—';
  const d = new Date(s);
  if (isNaN(d)) return '—';
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function DetailDrawer({ item, onClose, t }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="w-full max-w-xl h-full bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[var(--border-default)] px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            {item.sale_order_name || item.picking_name || t('outboundInv.detail_title')}
          </h2>
          <button onClick={onClose} className="p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={item.status} t={t} />
            {item.skip_reason && (
              <span className="text-xs text-[var(--text-tertiary)] border border-[var(--border-default)] rounded-full px-2 py-0.5">
                {t(`outboundInv.skip.${item.skip_reason}`)}
              </span>
            )}
            <span className="text-xs text-[var(--text-tertiary)]">{fmtDate(item.first_seen_at, t)}</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-[var(--text-tertiary)] mb-0.5">{t('outboundInv.col.order')}</div>
              <div className="text-sm text-[var(--text-primary)]">{item.sale_order_name || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-[var(--text-tertiary)] mb-0.5">{t('outboundInv.col.picking')}</div>
              <div className="text-sm text-[var(--text-primary)]">{item.picking_name || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-[var(--text-tertiary)] mb-0.5">{t('outboundInv.col.customer')}</div>
              <div className="text-sm text-[var(--text-primary)]">{item.partner_name || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-[var(--text-tertiary)] mb-0.5">{t('outboundInv.col.seen')}</div>
              <div className="text-sm text-[var(--text-primary)]">{fmtDate(item.last_seen_at, t)}</div>
            </div>
          </div>
          <div>
            <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('outboundInv.field.note')}</div>
            <div className="text-sm text-[var(--text-secondary)] whitespace-pre-wrap border border-[var(--border-default)] rounded-lg p-3 bg-[var(--bg-inset,#F8F9FC)]">
              {item.note || '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('outboundInv.field.case_text')}</div>
            <div className="text-sm text-[var(--text-secondary)] whitespace-pre-wrap border border-dashed border-[var(--border-default)] rounded-lg p-3">
              {item.case_text || '—'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OutboundInvestigations() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [pollState, setPollState] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);
  const [statusFilter, setStatusFilter] = useState('alle');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'alle') params.set('status', statusFilter);
      if (q) params.set('q', q);
      const data = await api(`/api/outbound-investigations?${params}`);
      setItems(data.items || []);
      setStats(data.stats || null);
      setPollState(data.poll_state || null);
      setDryRun(data.dry_run !== false);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  async function runPoll() {
    setRunning(true); setErr(null);
    try {
      await api('/api/outbound-investigations/run-poll', { method: 'POST' });
      await load();
    } catch (e) { setErr(e.message); }
    finally { setRunning(false); }
  }

  const kpis = useMemo(() => {
    if (!stats) return null;
    return [
      { key: 'would_create', label: t('outboundInv.kpi.would_create'), value: stats.would_create || 0, icon: PlusCircle, color: 'text-amber-600' },
      { key: 'seeded', label: t('outboundInv.kpi.seeded'), value: stats.seeded || 0, icon: Archive, color: 'text-slate-500' },
      { key: 'would_close', label: t('outboundInv.kpi.would_close'), value: stats.would_close || 0, icon: XCircle, color: 'text-indigo-600' },
      { key: 'resolved', label: t('outboundInv.kpi.resolved'), value: stats.resolved || 0, icon: CheckCircle2, color: 'text-emerald-600' },
    ];
  }, [stats, t]);

  const filters = ['alle', 'would_create', 'skipped', 'seeded', 'would_close', 'resolved'];

  return (
    <div className="px-6 py-6 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <PackageSearch className="w-6 h-6 text-[#338edc]" />
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">{t('outboundInv.page_title')}</h1>
        </div>
        <button
          onClick={runPoll}
          disabled={running}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--border-default)] text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {running ? t('outboundInv.run_poll_running') : t('outboundInv.run_poll')}
        </button>
      </div>

      <p className="text-sm text-[var(--text-tertiary)] max-w-3xl">{t('outboundInv.subtitle')}</p>

      {dryRun && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {t('outboundInv.dry_run_banner')}
        </div>
      )}

      {kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {kpis.map((k) => {
            const Ic = k.icon;
            return (
              <div key={k.key} className="border border-[var(--border-default)] rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs text-[var(--text-tertiary)]">{k.label}</div>
                  <div className="text-2xl font-semibold text-[var(--text-primary)]">{k.value}</div>
                </div>
                <Ic className={`w-6 h-6 ${k.color}`} />
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default)]">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1 text-sm rounded-[8px] ${statusFilter === f ? 'bg-white shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}
            >
              {t(`outboundInv.filter.${f}`) || f}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('outboundInv.search_placeholder')}
            className="pl-8 pr-3 py-1.5 text-sm border border-[var(--border-default)] rounded-lg"
          />
        </div>
        {pollState && (
          <span className="text-xs text-[var(--text-tertiary)] ml-auto">
            {t('outboundInv.poll_last')}: {fmtDate(pollState.last_poll_at, t)}
            {pollState.last_error_message ? ` · ${t('outboundInv.poll_error')}: ${pollState.last_error_message}` : ''}
          </span>
        )}
      </div>

      {err && <div className="p-3 rounded-lg bg-rose-50 text-rose-700 text-sm">{err}</div>}

      <div className="border border-[var(--border-default)] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)]">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">{t('outboundInv.col.order')}</th>
              <th className="text-left font-medium px-4 py-2.5">{t('outboundInv.col.picking')}</th>
              <th className="text-left font-medium px-4 py-2.5">{t('outboundInv.col.customer')}</th>
              <th className="text-left font-medium px-4 py-2.5">{t('outboundInv.col.note')}</th>
              <th className="text-left font-medium px-4 py-2.5">{t('outboundInv.col.status')}</th>
              <th className="text-left font-medium px-4 py-2.5">{t('outboundInv.col.seen')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#338edc] inline" /></td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--text-muted)]">{t('outboundInv.empty')}</td></tr>
            )}
            {!loading && items.map((r) => (
              <tr
                key={r.id}
                onClick={() => setSelected(r)}
                className="border-t border-[var(--border-default)] hover:bg-[var(--bg-inset,#F8F9FC)] cursor-pointer"
              >
                <td className="px-4 py-2.5 text-[var(--text-primary)] font-medium">{r.sale_order_name || '—'}</td>
                <td className="px-4 py-2.5 text-[var(--text-secondary)]">{r.picking_name || '—'}</td>
                <td className="px-4 py-2.5 text-[var(--text-secondary)]">{r.partner_name || '—'}</td>
                <td className="px-4 py-2.5 text-[var(--text-secondary)] max-w-[24rem] truncate" title={r.note || ''}>{r.note || '—'}</td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-col gap-1 items-start">
                    <StatusBadge status={r.status} t={t} />
                    {r.skip_reason && (
                      <span className="text-[11px] text-[var(--text-tertiary)]">{t(`outboundInv.skip.${r.skip_reason}`)}</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-[var(--text-tertiary)]">{fmtDate(r.last_seen_at, t)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <DetailDrawer item={selected} t={t} onClose={() => setSelected(null)} />}
    </div>
  );
}
